/**
 * Cognito password re-challenge via DCF API Gateway (not control login).
 * Encrypts password with RSA-OAEP before POST /dcf/signing/reauth.
 */

const pemToArrayBuffer = pem => {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '')
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

const importRsaPublicKey = async pem => {
  return window.crypto.subtle.importKey(
    'spki',
    pemToArrayBuffer(pem),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  )
}

export const encryptPasswordForReauth = async (password, publicKeyPem) => {
  const key = await importRsaPublicKey(publicKeyPem)
  const encoded = new TextEncoder().encode(password)
  const ciphertext = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, encoded)
  const bytes = new Uint8Array(ciphertext)
  let binary = ''
  bytes.forEach(b => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary)
}

/**
 * @param {Function} dispatch
 * @param {Function} requestFn redux request action creator
 * @param {string} password
 * @returns {Promise<{ reauth_proof: string, jti: string, expires_at: number }>}
 */
export const verifySignerPassword = async (dispatch, requestFn, password) => {
  if (!password) {
    throw new Error('Password is required')
  }

  const pubkey = await dispatch(
    requestFn({
      method: 'GET',
      url: '/dcf/signing/reauth-pubkey',
      errorMessage: 'Unable to load signing public key',
      hasLoader: false,
    }),
  )
  const publicKeyPem = pubkey?.public_key_pem || pubkey?.publicKeyPem
  const keyId = pubkey?.key_id || pubkey?.keyId
  if (!publicKeyPem) {
    throw new Error('Signing public key is not configured')
  }

  const passwordEncrypted = await encryptPasswordForReauth(password, publicKeyPem)
  const result = await dispatch(
    requestFn({
      method: 'POST',
      url: '/dcf/signing/reauth',
      body: JSON.stringify({
        password_encrypted: passwordEncrypted,
        key_id: keyId,
      }),
      errorMessage: 'Password verification failed',
      hasLoader: true,
      forceLoader: true,
    }),
  )

  if (!result?.reauth_proof) {
    throw new Error('Password verification failed')
  }
  return result
}

export default verifySignerPassword
